import { ApolloError, ApolloQueryResult, OperationVariables, useQuery } from "@apollo/client";
import { useState } from "react";
import { getActionPlanOrderBy } from "./helpers";
import { IActionPlan, IActionPlanTableOperationFilters } from "./ActionPlanEntityInterfaces";
import { getAllActionPlans, getAllActionPlansCount } from "api/queries/RCA/ActionPlanAPI";

/** Where clause shape for action plan list/count queries (Hasura-style filter object) */
export type ActionPlanListWhere = Record<string, unknown>;

export interface UseActionPlanListReturn {
    items: IActionPlan[];
    totalCount: number;
    loading: boolean;
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<unknown>>;
}

/**
 * Shared hook for the count-then-list action plan query pattern.
 * Used by GetAllActionPlans, GetAllUserActionPlans, and GetAllActionPlansForApproval.
 */
export function useActionPlanList(
    whereVariables: ActionPlanListWhere,
    tableOperationFilters: IActionPlanTableOperationFilters,
): UseActionPlanListReturn {
    const [items, setItems] = useState<IActionPlan[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    useQuery(getAllActionPlansCount, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            if (data?.action_plan_aggregate) {
                const count: number = data.action_plan_aggregate.aggregate?.count ?? 0;
                setTotalCount(count);
                if (count < 1) {
                    setItems([]);
                }
            }
        },
        variables: {
            where: whereVariables,
        },
    });

    const { loading, error, refetch } = useQuery(getAllActionPlans, {
        fetchPolicy: "no-cache",
        skip: !totalCount,
        onCompleted: (data) => {
            if (data?.action_plan && Array.isArray(data.action_plan)) {
                setItems(data.action_plan);
            }
        },
        variables: {
            limit: tableOperationFilters.limit,
            offset: tableOperationFilters.offset,
            order_by: getActionPlanOrderBy(tableOperationFilters),
            where: whereVariables,
        },
    });

    return {
        items,
        totalCount,
        loading,
        apolloQueryError: error,
        refetch,
    };
}
